import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { API_CONFIG, API_ENDPOINTS } from '../../config/api';
import { Button } from '@/components/ui/button';
import { getAccessToken } from '../../utils/authStorage';

interface Plan {
  id: number;
  plan_type: string;
  plan_name: string;
  max_conversations: number;
  base_tokens: number;
  flex_tokens: number;
  cost_per_100k_extra: number;
  allow_file_upload: boolean;
  allow_unlimited_usage: boolean;
  max_devices: number;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  stripe_product_name: string | null;
  price: {
    formatted: string;
    amount?: number;
    currency?: string;
    recurring?: {
      interval: string;
      interval_count: number;
    } | null;
  };
}

interface CurrentSubscription {
  id: number;
  subscription_type: string;
  plan_config: {
    plan_type: string;
    plan_name: string;
  };
  active: boolean;
  expires_at: string | null;
}

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan?: (planType: string) => void;
  title?: string;
}

// Descrições de marketing estáticas para cada plano
const planMarketingInfo: Record<string, { tagline: string; features: string[] }> = {
  alef: {
    tagline: 'Para quem está começando',
    features: [
      'Acesso a todas ferramentas',
      'Uso leve e devocional',
      'Estudo pessoal inicial',
      'Experimente a plataforma',
      '1.000 créditos/mês',
    ],
  },
  beit: {
    tagline: 'Para uso pessoal constante',
    features: [
      'Acesso a todas ferramentas',
      'Estudos e aulas semanais',
      'Preparos com mais profundidade',
      'Ideal para estudantes da Bíblia',
      '4.000 créditos/mês',
    ],
  },
  guimel: {
    tagline: 'Para ensino e pregação',
    features: [
      'Acesso a todas ferramentas',
      'Sermões e EBD frequentes',
      'Rotina intensa de ministério',
      'Ideal para pastores e líderes',
      '8.000 créditos/mês',
    ],
  },
  dalet: {
    tagline: 'Para alta demanda diária',
    features: [
      'Acesso a todas ferramentas',
      'Vários conteúdos por dia',
      'Produção teológica contínua',
      'Para pastores, teólogos e criadores',
      '16.000 créditos/mês',
    ],
  },
};

const PricingModal: React.FC<PricingModalProps> = ({ isOpen, onClose, onSelectPlan, title }) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError('');
      const token = getAccessToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.PLANS_WITH_SUBSCRIPTION}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Filtrar apenas planos ativos e ordenar por tipo e periodicidade
        const planOrder = ['freemium', 'intermediario', 'avancado', 'beit', 'guimel', 'dalet', 'alef'];
        const intervalOrder = ['month', 'year'];
        
        const sortedPlans = (data.plans || [])
          .filter((plan: Plan) => {
            const isFree = plan.price.formatted === 'Grátis' || plan.plan_type === 'freemium' || plan.price.amount === 0;
            return plan.plan_name && plan.plan_name.trim() !== '' && !isFree;
          })
          .sort((a: Plan, b: Plan) => {
            const indexA = planOrder.indexOf(a.plan_type) >= 0 ? planOrder.indexOf(a.plan_type) : 999;
            const indexB = planOrder.indexOf(b.plan_type) >= 0 ? planOrder.indexOf(b.plan_type) : 999;
            if (indexA !== indexB) return indexA - indexB;
            const intervalA = a.price.recurring?.interval || '';
            const intervalB = b.price.recurring?.interval || '';
            const intervalIndexA = intervalOrder.indexOf(intervalA);
            const intervalIndexB = intervalOrder.indexOf(intervalB);
            if (intervalIndexA !== intervalIndexB) return intervalIndexA - intervalIndexB;
            return (a.price.amount || 0) - (b.price.amount || 0);
          });
        
        setPlans(sortedPlans);
        setCurrentSubscription(data.current_subscription || null);
        setSelectedVariantByPlanKey({});
      } else {
        setError('Erro ao carregar planos. Tente novamente.');
      }
    } catch (err) {
      console.error('Error loading plans:', err);
      setError('Erro ao carregar planos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatTokens = (tokens: number): string => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(0)}K`;
    }
    return tokens.toString();
  };

  type IntervalKey = 'mensal' | 'semestral' | 'anual';
  const getIntervalKey = (p: Plan): IntervalKey => {
    const r = p.price.recurring;
    if (!r) return 'mensal';
    if (r.interval === 'month' && r.interval_count === 6) return 'semestral';
    if (r.interval === 'year' && r.interval_count === 1) return 'anual';
    return 'mensal';
  };

  const getPlanBaseKey = (p: Plan): string => {
    const n = (p.plan_name || '').trim();
    const base = n
      .replace(/\s*-\s*(Mensal|Semestral|Anual)$/i, '')
      .trim() || p.plan_type;
    return base.toLowerCase();
  };

  const groupPlansByBase = (list: Plan[]): Map<string, Plan[]> => {
    const m = new Map<string, Plan[]>();
    for (const p of list) {
      const key = getPlanBaseKey(p);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(p);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const order: IntervalKey[] = ['mensal', 'semestral', 'anual'];
        return order.indexOf(getIntervalKey(a)) - order.indexOf(getIntervalKey(b));
      });
    }
    return m;
  };

  const [selectedVariantByPlanKey, setSelectedVariantByPlanKey] = useState<Record<string, number>>({});

  const planGroups = groupPlansByBase(plans);
  const planBaseKeys = Array.from(planGroups.keys());

  const setVariant = (baseKey: string, index: number) => {
    setSelectedVariantByPlanKey((prev) => ({ ...prev, [baseKey]: index }));
  };

  const getIntervalLabel = (k: IntervalKey): string => {
    return { mensal: 'Mensal', semestral: 'Semestral', anual: 'Anual' }[k];
  };

  const getPriceSuffix = (k: IntervalKey): string => {
    return { mensal: '/mês', semestral: '/semestre', anual: '/ano' }[k];
  };

  const isCurrentPlan = (planType: string): boolean => {
    return currentSubscription?.plan_config?.plan_type === planType;
  };

  const handlePlanSelect = async (plan: Plan) => {
    try {
      setError(''); // Limpar erros anteriores
      const token = getAccessToken();
      // Criar checkout session no backend
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.CREATE_CHECKOUT_SESSION}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_type: plan.plan_type,
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Redirecionar para checkout do Stripe
        window.location.href = data.checkout_url;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Erro ao criar sessão de checkout');
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Erro ao criar sessão de checkout. Tente novamente.');
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex min-h-screen min-w-full items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pricing-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-lg shadow-2xl p-8 w-full max-w-6xl border-2 border-brand-accent transform transition-all duration-200 scale-100 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative flex items-center justify-center mb-6">
          <h2 id="pricing-modal-title" className="text-3xl font-bold text-brand-text-primary text-center">
            {title ?? 'Escolha seu Plano de Upgrade!'}
          </h2>
          <button
            onClick={onClose}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-brand-text-secondary hover:text-brand-text-primary transition-colors p-2 hover:bg-muted rounded-lg"
            aria-label="Fechar modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Current Plan Pill - Abaixo do Título */}
        {!loading && !error && currentSubscription && (
          <div className="mb-6 flex justify-center">
            <div className="inline-block bg-brand-accent/20 text-brand-accent border border-brand-accent text-sm font-semibold px-4 py-2 rounded-full">
              Você está usando o plano <strong>{(currentSubscription.plan_config?.plan_name || '').replace(/Gratuíto/g, 'Gratuito').replace(/gratuíto/g, 'gratuito')}</strong>
              {currentSubscription.expires_at && (
                <span className="ml-2 text-brand-text-secondary">
                  • Expira em {new Date(currentSubscription.expires_at).toLocaleDateString('pt-BR')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        {/* Plans Grid */}
        {!loading && !error && planBaseKeys.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {planBaseKeys.map((baseKey) => {
              const variants = planGroups.get(baseKey)!;
              const selectedIndex = selectedVariantByPlanKey[baseKey] ?? 0;
              const plan = variants[selectedIndex] ?? variants[0];
              const isCurrent = variants.some((v) => isCurrentPlan(v.plan_type));
              const baseName = (variants[0].plan_name || '').replace(/\s*-\s*(Mensal|Semestral|Anual)$/i, '').trim() || variants[0].plan_type;
              const isPopular = baseName.toLowerCase().includes('guimel');
              const isGuimel = baseName.toLowerCase().includes('guimel');
              const hasMultiple = variants.length > 1;

              return (
                <div
                  key={baseKey}
                  className={`relative rounded-lg border-2 p-6 transition-all duration-200 ${
                    isCurrent
                      ? 'border-brand-accent bg-muted'
                      : isPopular
                      ? 'border-brand-primary bg-card'
                      : 'border-brand-border bg-card'
                  } hover:shadow-xl hover:scale-105`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                      Mais Popular
                    </div>
                  )}

                  <div className="mb-4 text-center">
                    <h3 className="text-2xl font-bold text-brand-text-primary mb-1">
                      {baseName}
                    </h3>
                    
                    {/* Tagline de marketing */}
                    {planMarketingInfo[baseKey]?.tagline && (
                      <p className="text-sm text-brand-accent font-medium mb-3">
                        {planMarketingInfo[baseKey].tagline}
                      </p>
                    )}

                    {hasMultiple && (
                      <div className="flex flex-wrap justify-center gap-1 mb-3" role="group" aria-label="Periodicidade">
                        {variants.map((v, idx) => {
                          const ik = getIntervalKey(v);
                          const active = idx === selectedIndex;
                          return (
                            <button
                              key={`${baseKey}-${ik}`}
                              type="button"
                              onClick={() => setVariant(baseKey, idx)}
                              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                active
                                  ? 'bg-accent text-accent-foreground'
                                  : 'bg-muted text-muted-foreground hover:bg-muted'
                              }`}
                            >
                              {getIntervalLabel(ik)}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-brand-text-primary">
                        {plan.price.formatted}
                      </span>
                      <span className="text-brand-text-secondary ml-2">
                        {getPriceSuffix(getIntervalKey(plan))}
                      </span>
                    </div>
                    {isCurrent && (
                      <div className="mt-3 inline-block bg-muted text-muted-foreground text-xs font-semibold px-3 py-1 rounded-full">
                        Seu Plano
                      </div>
                    )}
                  </div>

                  {/* Features de marketing (estáticas) */}
                  <ul className="space-y-3 mb-6">
                    {planMarketingInfo[baseKey]?.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckIcon className="w-5 h-5 text-brand-accent mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-brand-text-secondary text-sm">{feature}</span>
                      </li>
                    )) || (
                      <>
                        <li className="flex items-start">
                          <CheckIcon className="w-5 h-5 text-brand-accent mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-brand-text-secondary text-sm">
                            <strong>{formatTokens(plan.base_tokens)}</strong> tokens base
                          </span>
                        </li>
                        <li className="flex items-start">
                          <CheckIcon className="w-5 h-5 text-brand-accent mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-brand-text-secondary text-sm">
                            <strong>{plan.max_devices}</strong> dispositivo{plan.max_devices !== 1 ? 's' : ''}
                          </span>
                        </li>
                      </>
                    )}
                  </ul>

                  <Button
                    onClick={() => handlePlanSelect(plan)}
                    disabled={isCurrent}
                    variant={isCurrent ? 'secondary' : isGuimel ? 'outline' : 'hero'}
                    className="w-full"
                  >
                    {isCurrent ? 'Plano Atual' : 'Assinar este plano agora!'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PricingModal;

